import { StanButton, StanInput, StanModal } from '@/components';
import { autoAddHttps } from '@/lib';
import { Editor } from '@tiptap/react';
import { useState } from 'react';
import { FiLink, FiUpload } from 'react-icons/fi';
import { GoVideo } from 'react-icons/go';

export const VideoUploadModal = ({
    isOpen,
    onClose,
    editor,
    onUploadVideo,
}: {
    isOpen: boolean;
    onClose: () => void;
    editor: Editor;
    onUploadVideo: () => void;
}) => {
    const [videoUrl, setVideoUrl] = useState('');
    const [error, setError] = useState('');

    const handleClose = () => {
        setVideoUrl('');
        setError('');
        onClose();
    };

    const getEmbedHTML = (url: string): string => {
        let videoId = '';
        let platform = '';
        let queryParams = '';

        // Check for Loom URL
        const loomRegex = /https:\/\/www\.loom\.com\/share\/([a-zA-Z0-9]+)/;
        const loomMatch = url.match(loomRegex);
        if (loomMatch) {
            videoId = loomMatch[1] || '';
            platform = 'loom';
        }

        // Check for YouTube URL
        const youtubeRegex =
            /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:shorts\/|[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?|watch)(?:\/|(?:\?v=))|.*[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})((?:[?&].*)?)/;
        const youtubeMatch = url.match(youtubeRegex);
        if (youtubeMatch) {
            videoId = youtubeMatch[1] || '';
            platform = 'youtube';
        }

        // Check for Wistia URL
        const wistiaRegex =
            /(?:https?:\/\/)?(?:www\.)?(?:wistia\.com\/medias\/|fast\.wistia\.net\/embed\/medias\/)([a-zA-Z0-9]+)(?:[?&].*)?/;
        const wistiaMatch = url.match(wistiaRegex);
        if (wistiaMatch) {
            videoId = wistiaMatch[1] || '';
            platform = 'wistia';
        }

        // Check for Vimeo URL
        const vimeoRegex =
            /(?:https?:\/\/)?(?:www\.)?(?:vimeo\.com\/|player\.vimeo\.com\/video\/)([0-9]+)((?:[?&].*)?)/;
        const vimeoMatch = url.match(vimeoRegex);
        if (vimeoMatch) {
            videoId = vimeoMatch[1] || '';
            queryParams = vimeoMatch[2] || '';
            platform = 'vimeo';
        }

        // Generate the embed HTML based on the platform
        if (videoId && platform) {
            switch (platform) {
                case 'loom':
                    return `<iframe src="https://www.loom.com/embed/${videoId}" frameborder="0" webkitallowfullscreen mozallowfullscreen allowfullscreen style="width: 100%; height: 400px;"></iframe>`;
                case 'youtube':
                    return `<iframe src="https://www.youtube.com/embed/${videoId}" frameborder="0" allowfullscreen style="width: 100%; height: 400px;"></iframe>`;
                case 'wistia':
                    return `<iframe src="https://fast.wistia.net/embed/iframe/${videoId}" frameborder="0" allow="autoplay fullscreen" allowfullscreen style="width: 100%; height: 400px;"></iframe>`;
                case 'vimeo':
                    return `<iframe src="https://player.vimeo.com/video/${videoId}${queryParams}" frameborder="0" allow="autoplay; fullscreen" allowfullscreen style="width: 100%; height: 400px;"></iframe>`;
                default:
                    return '';
            }
        }

        return '';
    };

    const handleSubmit = () => {
        if (!videoUrl.trim()) {
            setError('Please enter a video link.');
            return;
        }

        const processedUrl = autoAddHttps(videoUrl.trim());
        const embedHTML = getEmbedHTML(processedUrl);

        if (embedHTML) {
            editor.chain().focus().insertContent(embedHTML).run();
            handleClose();
        } else {
            setError('Invalid video link. Please use a YouTube, Loom, Wistia, or Vimeo URL.');
        }
    };

    const handleUploadVideo = () => {
        handleClose();
        onUploadVideo();
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setVideoUrl(e.target.value);
        if (error) {
            setError(''); // Clear error when user starts typing
        }
    };

    return (
        <StanModal id='video-upload-modal' isOpen={isOpen} onClose={handleClose} title='Add a Video' size='xs'>
            <div className='flex flex-col gap-4'>
                <StanInput
                    innerLabel='Video URL'
                    name='video-url'
                    type='text'
                    value={videoUrl}
                    onChange={handleInputChange}
                    startIcon={FiLink}
                    placeholder='Paste a YouTube, Loom, Wistia, or Vimeo video link here...'
                    error={!!error}
                    hintText={error && error}
                />

                <div className='flex flex-col gap-2'>
                    <StanButton
                        name='embed-video-button'
                        size='sm'
                        disabled={!videoUrl.trim()}
                        onClick={handleSubmit}
                        icon={GoVideo}
                    >
                        Embed Video
                    </StanButton>

                    <StanButton
                        name='upload-video-button'
                        size='sm'
                        variant='outline'
                        onClick={handleUploadVideo}
                        icon={FiUpload}
                    >
                        Upload Your Own
                    </StanButton>
                </div>
            </div>
        </StanModal>
    );
};
